import { InputAdornment, Stack, SxProps, TextField } from "@mui/material";
import { ReactElement } from "react";
import { MAIN_FONT_FAMILY } from "../../data/constant/Constants";
import {
  COLOR_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
} from "../../data/colors/Colors";
import { Theme } from "@emotion/react";
import { CustomTextView } from "./CustomTextView";

interface props {
  type?: React.HTMLInputTypeAttribute;
  placeholder: string;
  variant?: "outlined" | "standard";
  onTextChange: (text: string) => void;
  isFullWidth?: boolean;
  startIcon?: ReactElement;
  endIcon?: ReactElement;
  borderRadius?: number;
  textColor?: string;
  backgroundColor?: string;
  onHoverBorderColor?: string;
  borderColor?: string;
  onFocusBorderColor?: string;
  width?: number;
  height?: number;
  error?: boolean;
  helperText?: string;
  sxProps?: SxProps<Theme>;
  fontSize?: number | string;
  fontWeight?: string;
  value?: string;
  multiLine?: boolean;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  ref?: any;
}

export const CustomTextField: React.FC<props> = ({
  type = "text",
  variant = "outlined",
  placeholder,
  onTextChange,
  isFullWidth = true,
  startIcon,
  endIcon,
  value,
  multiLine = false,
  borderRadius = 12,
  textColor = COLOR_LIGHT_GRAY,
  backgroundColor = COLOR_VERY_LIGHT_GRAY,
  onHoverBorderColor = COLOR_MAIN,
  borderColor = COLOR_BLACK,
  onFocusBorderColor = COLOR_MAIN,
  width,
  height,
  error,
  helperText,
  sxProps,
  fontSize = 16,
  fontWeight,
  required,
  disabled = false,
  maxLength,
  ref,
}: props) => {
  return (
    <Stack>
      <TextField
        ref={ref}
        type={type}
        value={value}
        placeholder={placeholder}
        variant={variant}
        onChange={(txt) => {
          if (
            maxLength != undefined &&
            txt.currentTarget.value.length > maxLength
          )
            return;
          onTextChange(txt.currentTarget.value);
        }}
        autoComplete="off"
        fullWidth={isFullWidth}
        multiline={multiLine}
        error={error}
        helperText={helperText}
        slotProps={{
          formHelperText: { style: { fontSize: 12, textAlign: "start" } },
          input: {
            startAdornment: (
              <InputAdornment position="start">{startIcon}</InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">{endIcon}</InputAdornment>
            ),
            sx: {
              borderRadius,
              height,
            },
          },
          inputLabel: {
            sx: {
              fontFamily: MAIN_FONT_FAMILY,
              color: textColor,
              background: backgroundColor,
              width,
              fontWeight,
              fontSize,
            },
          },
        }}
        required={required}
        disabled={disabled}
        sx={[
          disabled
            ? {}
            : {
                "& .MuiOutlinedInput-root": {
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: onHoverBorderColor,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: borderColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: onFocusBorderColor,
                  },
                },
              },
          ...(Array.isArray(sxProps) ? sxProps : [sxProps]),
        ]}
      />
      {maxLength && (
        <CustomTextView
          text={`${value?.length ?? 0}/${maxLength}`}
          variant="caption"
          props={{ alignSelf: "end", pl: 2, pr: 2, pt: 0.5 }}
        />
      )}
    </Stack>
  );
};
